/**
 * browser.ts
 * ---------------------------------------------------------------------------
 * Клиентский слой компонентов (бывш. ep-research/assets/js/components.js).
 * Логика рендера (config → HTML) — в общем render.ts (его же использует
 * Node-прокси для SSR). Здесь остаётся ТОЛЬКО:
 *
 *   • fallback-рендер: если прокси не отработал (локальный Ghost, превью,
 *     прокси не активен) — рисуем плейсхолдеры на клиенте с inline LD;
 *   • гидратация интерактива: сортировка таблиц (работает и на SSR-разметке
 *     через делегирование событий).
 *
 * Когда прокси активен, разметка уже в семантических тегах, а mount() селектит
 * только плейсхолдеры div[data-component] с дочерним <script application/json>,
 * поэтому двойного рендера не происходит.
 *
 * Собирается esbuild'ом в IIFE → ep-research/assets/built/components.js.
 * ---------------------------------------------------------------------------
 */

import { renderComponent, RENDERERS } from './render';
import type { ComponentType } from './types';

function esc(s: unknown): string {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => {
        const map: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
        return map[c]!;
    });
}

function readConfig(host: Element): Record<string, any> {
    const script = host.querySelector('script[type="application/json"]');
    if (!script) return {};
    try {
        return JSON.parse(script.textContent || '');
    } catch (err) {
        console.warn('[components] bad JSON in', host, err);
        return { __error: 'Invalid JSON config' };
    }
}

/* 1-based номер host'а среди компонентов того же типа в DOM-порядке.
   Считаем по всему документу, включая уже отрендеренные прокси-блоки (они
   сохраняют свой data-component): на гибридной странице, где часть блоков
   пришла из SSR, нумерация обязана совпасть с серверной. Обходим [data-component]
   вручную вместо селектора по значению — не нужно экранировать значение
   атрибута, пришедшее из разметки. */
function ordinalOf(host: Element, type: string): number {
    const all = document.querySelectorAll('[data-component]');
    let n = 0;
    for (let i = 0; i < all.length; i++) {
        const el = all[i]!;
        if (el.getAttribute('data-component') === type) n++;
        if (el === host) break;
    }
    return n || 1;
}

function fromString(html: string): Element | null {
    const tpl = document.createElement('template');
    tpl.innerHTML = String(html || '').trim();
    return tpl.content.firstElementChild;
}

/* ── render (fallback) ────────────────────────────────────────────── */

function render(host: HTMLElement): void {
    // Skip already-rendered (e.g. our own output, которое сохраняет data-component)
    if (host.dataset.componentRendered === '1') return;

    const type = host.dataset.component;
    if (!type || !RENDERERS[type as ComponentType]) return;

    // Рендерим только плейсхолдеры: div с inline <script type="application/json">.
    // SSR-вывод прокси — семантические теги без такого скрипта, его не трогаем.
    if (host.tagName !== 'DIV' || !host.querySelector(':scope > script[type="application/json"]')) {
        return;
    }

    const config = readConfig(host);
    if (config.__error) {
        host.outerHTML = '<div class="component-error" data-component="' + esc(type) + '">'
            + 'Invalid JSON in component "' + esc(type) + '"</div>';
        return;
    }

    const newNode = fromString(renderComponent(type, config, {
        inlineLd: true,
        siteUrl: location.origin,
        pageUrl: location.href.split('#')[0],
        ordinal: ordinalOf(host, type),
    }));
    if (!newNode) return;
    (newNode as HTMLElement).dataset.componentRendered = '1';
    host.parentNode?.replaceChild(newNode, host);
}

function mount(root?: Document | Element): void {
    const hosts = (root || document).querySelectorAll('div[data-component]');
    hosts.forEach((h) => render(h as HTMLElement));
}

/* ── interactions (event delegation; работает и на SSR-таблицах) ──── */

function sortBenchmarkTable(table: HTMLTableElement, colIndex: number, dir: string): void {
    const tbody = table.querySelector('tbody');
    if (!tbody) return;
    const rows = Array.prototype.slice.call(tbody.querySelectorAll('tr')) as HTMLTableRowElement[];
    const numeric = rows.every((r) => {
        const t = (r.children[colIndex]?.textContent || '').trim().replace(/[%$,\s]/g, '');
        return t === '' || !isNaN(parseFloat(t));
    });

    rows.sort((a, b) => {
        let av: any = (a.children[colIndex]?.textContent || '').trim();
        let bv: any = (b.children[colIndex]?.textContent || '').trim();
        if (numeric) {
            av = parseFloat(av.replace(/[%$,\s]/g, '')) || 0;
            bv = parseFloat(bv.replace(/[%$,\s]/g, '')) || 0;
            return dir === 'asc' ? av - bv : bv - av;
        }
        return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    rows.forEach((r) => tbody.appendChild(r));
}

function onClick(e: MouseEvent): void {
    const target = e.target as Element | null;
    const th = target?.closest('th.benchmark-table__header') as HTMLElement | null;
    if (!th) return;
    const table = th.closest('.benchmark-table') as HTMLElement | null;
    if (!table || table.dataset.sortable === 'false') return;

    const thead = th.parentNode as Element;
    const headers = thead.querySelectorAll('th');
    const current = th.dataset.sortDir;
    const next = current === 'desc' ? 'asc' : 'desc';
    headers.forEach((h) => h.removeAttribute('data-sort-dir'));
    th.dataset.sortDir = next;

    const col = parseInt(th.dataset.col || '', 10) || 0;
    const tableEl = table.querySelector('table') as HTMLTableElement | null;
    if (tableEl) sortBenchmarkTable(tableEl, col, next);
}

/* ── boot ─────────────────────────────────────────────────────────── */

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { mount(); });
} else {
    mount();
}
document.addEventListener('click', onClick);

// expose for debugging / re-render after dynamic content
(window as any).epResearchComponents = { mount };
